/**
 * Formatting helpers shared by every Intake report surface.
 *
 * Ported from the design source `compass-oppreport.jsx` (`fmtUSD`) in Claude
 * Design project `79357508-fe09-4122-831f-a0014c6a31b0`. Fixed by
 * `docs/frontend/intake-report-port-contract.md` §1. Phase 1 owns this file.
 */

/** The em dash every uncomputed value renders. */
export const REPORT_EMPTY_VALUE = "—";

/** Below this, thousands keep one decimal — see `formatReportUsd`. */
const THOUSANDS_PRECISION_CEILING = 10;

/** `1.20` → `1.2`, `10.0` → `10`, `1.05` → `1.05`. */
function stripTrailingZeros(fixed: string): string {
  return fixed.replace(/0+$/, "").replace(/\.$/, "");
}

/**
 * Design `fmtUSD`: `$1.2M` / `$225K` / `$32`, negatives prefixed `-`.
 *
 * The millions branch keeps the design's two-decimal round then strips the
 * trailing zeros and any orphaned decimal point, so `1_200_000` renders
 * `$1.2M` and `1_000_000` renders `$1M`, while `1_050_000` keeps `$1.05M`.
 *
 * The thousands branch **diverges from the design**, which rounds to whole
 * thousands at every magnitude. That is fine for the design's own numbers
 * (`$225K`, `$375K`) but not for the low thousands a real cost base lands in:
 * `$1,222` rendered `$1K`, a 19% error, and the same value rendered `$1.2K`
 * as a formula token in the trace modal one line away — two formatters
 * disagreeing in one view. Under $10K this keeps one decimal, so the two
 * agree; above it the design's rounding stands.
 */
export function formatReportUsd(value: number | null | undefined): string {
  if (value == null || Number.isNaN(value)) return REPORT_EMPTY_VALUE;
  const sign = value < 0 ? "-" : "";
  const magnitude = Math.abs(value);
  if (magnitude >= 1e6) {
    const millions = stripTrailingZeros((magnitude / 1e6).toFixed(2));
    return `${sign}$${millions}M`;
  }
  if (magnitude >= 1e3) {
    const thousands = magnitude / 1e3;
    // `.toFixed(1)` can carry into the next order (9_990 → "10.0"), which is
    // exactly the wanted answer — strip the zero and it reads `$10K`.
    const rendered =
      thousands < THOUSANDS_PRECISION_CEILING
        ? stripTrailingZeros(thousands.toFixed(1))
        : String(Math.round(thousands));
    return `${sign}$${rendered}K`;
  }
  return `${sign}$${Math.round(magnitude)}`;
}

/**
 * Design horizon label: `"3 years"` / `"1 year"`.
 *
 * `null` in ⇒ `null` out, so callers can fall back to their own copy rather
 * than render a horizon the model does not have (§5 gap #1).
 */
export function formatHorizonYears(years: number | null): string | null {
  if (years == null) return null;
  return `${years} ${years === 1 ? "year" : "years"}`;
}

/**
 * Design `F.roiPct`: a whole-number percent.
 *
 * `summary.roiPercent` is not rounded upstream, so a model with a tiny cost
 * base carries a dozen digits ("5965464.864157%"). Rounding lives here rather
 * than at each call site because the figure tile and an executive-summary
 * `{ kind: "figure", key: "roi" }` reference render the same number in the
 * same report — two roundings is two answers.
 */
export function formatReportRoiPercent(
  percent: number | null | undefined,
): string {
  if (percent == null || Number.isNaN(percent)) return REPORT_EMPTY_VALUE;
  return `${Math.round(percent)}%`;
}
